import React from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useMotionTemplate, useMotionValue, motion } from "framer-motion";

// Custom styles for phone input
const phoneInputStyles = `
  .react-tel-input .selected-flag {
    background-color: #27272a !important;
    border: none !important;
  }
  
  .react-tel-input .country-list {
    background-color: #27272a !important;
    border: none !important;
  }
  
  .react-tel-input .country-list .country {
    color: #ffffff !important;
    background-color: #27272a !important;
  }
  
  .react-tel-input .country-list .country:hover {
    background-color: #3f3f46 !important;
    color: #fff !important;
  }
  
  .react-tel-input .country-list .country.highlight {
    background-color: #3f3f46 !important;
    color: #fff !important;
  }
  
  .react-tel-input .search-box {
    display: flex !important;
    align-items: center !important;
    padding: 8px 12px !important;
  }
  
  .react-tel-input .search-box input {
    color: #9ca3af !important;
    background-color: #27272a !important;
    border: none !important;
    padding-left: 8px !important;
  }
  
  .react-tel-input .search-box .search-icon {
    color: #9ca3af !important;
  }
`;

interface FormInputProps {
  label: string;
  name: string;
  placeholder?: string;
  className?: string;
  control: Control;
  rules?: RegisterOptions;
  isRequired?: boolean;
}

export const PhoneNumberInput = ({
  label,
  placeholder,
  name,
  rules = {},
  control,
  isRequired = false,
}: FormInputProps) => {
  const radius = 100;
  const [visible, setVisible] = React.useState(false);

  let mouseX = useMotionValue(0);
  let mouseY = useMotionValue(0);

  // Inject custom styles
  React.useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = phoneInputStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  function handleMouseMove({ currentTarget, clientX, clientY }: any) {
    let { left, top } = currentTarget.getBoundingClientRect();

    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: {
          value: isRequired,
          message: `${label || name} is required`,
        },
        ...rules,
      }}
      render={({
        field: { value, onChange, ...rest },
        fieldState: { error },
      }) => (
        <div className="flex flex-col sm:gap-2">
          <label className="text-sm md:text-base text-neutral-200">
            {label}
            {isRequired && (
              <span className="text-red-400 font-bold ml-2">*</span>
            )}
          </label>
          <motion.div
            style={{
              background: useMotionTemplate`
                radial-gradient(
                  ${visible ? radius + "px" : "0px"} circle at ${mouseX}px ${mouseY}px,
                  #3b82f6,
                  transparent 80%
                )
              `,
            }}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            className="group/input rounded-lg p-[2px] transition duration-300"
          >
            <PhoneInput
              country={"us"}
              value={value || ""}
              onChange={(_, __, ___, formattedValue) =>
                onChange(formattedValue)
              }
              placeholder={placeholder}
              enableSearch
              countryCodeEditable={false}
              inputClass="!w-full !h-10 !text-sm md:!text-base !bg-zinc-800 !text-gray-200 !border-none !rounded-md !px-14 !py-2 !shadow-[0px_0px_1px_1px_#404040] focus:!outline-none focus:!ring-2 focus:!ring-neutral-600 group-hover/input:!shadow-none"
              buttonClass="!bg-zinc-800 !border-none !rounded-l-md"
              dropdownClass="!bg-zinc-800 !border-none !rounded-md"
              containerClass="!w-full"
              searchClass="!bg-zinc-800 !text-gray-200 !border-none !rounded-md !px-3 !py-2 !flex !items-center"
              searchStyle={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px'
              }}
              searchPlaceholder="Search countries"
              preferredCountries={['us', 'in']}
              specialLabel=""
              {...rest}
            />
          </motion.div>
          {error?.message && (
            <p className="text-red-400 text-xs mt-1">{error?.message}</p>
          )}
        </div>
      )}
    />
  );
};
